/**
 * <bundle-card-thumbs>
 * Lazily fetches a few products from each bundle step (collection or product list)
 * and cross-fades through their images inside the per-step thumbnail tiles to
 * represent the "selection" you'd make in the bundle drawer.
 *
 * Markup (rendered by snippets/bundle-grid-card.liquid):
 *   <bundle-card-thumbs data-interval="2200">
 *     <span class="bundle-card__thumb" data-source-type="collection"
 *           data-collection-handle="bundle-tanks" data-limit="5">
 *       <img class="bundle-card__thumb-img" src="...">  <!-- optional SSR fallback -->
 *     </span>
 *     ...
 *   </bundle-card-thumbs>
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('bundle-card-thumbs')) return;

  var REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // handle/key -> Promise<string[] | string>
  var cache = {};

  function withWidth(src, width) {
    if (!src) return src;
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  function fetchCollectionImages(handle, limit) {
    if (!handle) return Promise.resolve([]);
    var key = 'col:' + handle + ':' + limit;
    if (cache[key]) return cache[key];
    var url =
      ROOT +
      'collections/' +
      encodeURIComponent(handle) +
      '/products.json?limit=' +
      (limit || 5);
    cache[key] = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        return (data.products || [])
          .map(function (p) {
            return (p.images && p.images[0] && p.images[0].src) || p.featured_image || '';
          })
          .filter(Boolean);
      })
      .catch(function () {
        return [];
      });
    return cache[key];
  }

  function fetchProductImage(handle) {
    if (!handle) return Promise.resolve('');
    var key = 'prod:' + handle;
    if (cache[key]) return cache[key];
    var url = ROOT + 'products/' + encodeURIComponent(handle) + '.js';
    cache[key] = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p) return '';
        return p.featured_image || (p.images && p.images[0]) || '';
      })
      .catch(function () {
        return '';
      });
    return cache[key];
  }

  function fetchProductImages(handles, limit) {
    var list = (handles || []).slice(0, limit || 5);
    if (!list.length) return Promise.resolve([]);
    return Promise.all(list.map(fetchProductImage)).then(function (arr) {
      return arr.filter(Boolean);
    });
  }

  function imagesForThumb(thumb) {
    var sourceType = thumb.getAttribute('data-source-type') || 'collection';
    var limit = parseInt(thumb.getAttribute('data-limit'), 10) || 5;
    if (sourceType === 'products') {
      var handles = (thumb.getAttribute('data-product-handles') || '')
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
      return fetchProductImages(handles, limit);
    }
    return fetchCollectionImages(thumb.getAttribute('data-collection-handle'), limit);
  }

  function setupThumb(thumb, interval, offset) {
    imagesForThumb(thumb).then(function (srcs) {
      if (!srcs || !srcs.length) return;
      srcs = srcs.map(function (s) {
        return withWidth(s, 128);
      });

      var imgA = thumb.querySelector('.bundle-card__thumb-img');
      if (!imgA) {
        imgA = document.createElement('img');
        imgA.className = 'bundle-card__thumb-img';
        imgA.alt = '';
        imgA.decoding = 'async';
        thumb.appendChild(imgA);
      }
      if (!imgA.getAttribute('src')) imgA.src = srcs[0];

      if (REDUCED || srcs.length < 2) return;

      // Preload so cross-fades are instant.
      srcs.forEach(function (s) {
        var pre = new Image();
        pre.src = s;
      });

      var imgB = document.createElement('img');
      imgB.className = 'bundle-card__thumb-img';
      imgB.alt = '';
      imgB.decoding = 'async';
      imgB.style.opacity = '0';
      thumb.appendChild(imgB);

      var imgs = [imgA, imgB];
      var front = 0;
      var idx = 0;

      function tick() {
        idx = (idx + 1) % srcs.length;
        var back = 1 - front;
        var nextImg = imgs[back];
        nextImg.onload = function () {
          nextImg.style.opacity = '1';
          imgs[front].style.opacity = '0';
          front = back;
        };
        nextImg.src = srcs[idx];
      }

      thumb._bgcTimer = setTimeout(function () {
        thumb._bgcTimer = setInterval(tick, interval);
      }, offset);
    });
  }

  class BundleCardThumbs extends HTMLElement {
    connectedCallback() {
      if (this._bgcInit) return;
      const interval = parseInt(this.getAttribute('data-interval'), 10) || 2200;

      const start = () => {
        if (this._bgcInit) return;
        this._bgcInit = true;
        this.querySelectorAll('.bundle-card__thumb').forEach((thumb, i) => {
          setupThumb(thumb, interval, i * 300);
        });
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                start();
                io.disconnect();
              }
            });
          },
          { rootMargin: '200px' }
        );
        io.observe(this);
      } else {
        start();
      }
    }

    disconnectedCallback() {
      this.querySelectorAll('.bundle-card__thumb').forEach((thumb) => {
        if (thumb._bgcTimer) {
          clearTimeout(thumb._bgcTimer);
          clearInterval(thumb._bgcTimer);
        }
      });
    }
  }

  customElements.define('bundle-card-thumbs', BundleCardThumbs);
})();
